import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/design';
import { RegistrationPage, useAuth } from '@/domains/auth';
import { surveyApi } from '../api';
import { PORTAL_PATHS, teamRoutingPath } from '../paths';
import { asksFirst, teamsCycleOf } from './start-survey-card';

/**
 * `PORTAL_PATHS.addSurvey` — a survey added to the account signed in, by registering another
 * number: the registration page in its `account` mode (the teams onboarding plan's R7).
 *
 * **The page is registration's, not a second one.** Its steps, its refusals and its resume are
 * `/registration`'s, rendered from the view survey-api hands it; what this module adds is the one
 * thing only the survey domain can say — which of the account's participants a number already
 * belongs to, since `/my/participants` is read here and nowhere in `domains/auth`. Keyed by the
 * account, so a sign-in as somebody else in this tab starts the page over rather than showing
 * the last account's run under the new one.
 *
 * Mounted inside the Advisor Portal's zone, whose element is the guard: staff and an
 * administrator meet the boundary page at this address, never the page. That is a render
 * decision — survey-api would bind a staff session's run as readily, and that is not something
 * the product offers.
 */
export function AddSurveyPage() {
  const { user } = useAuth();
  // Unreachable under `ProtectedRoute` and the zone's guard, which both wait for `user`.
  if (!user) return null;

  return (
    <>
      <PageHeader
        title="Add a survey"
        lede="Register another number and its survey is added to this account — no new sign-in is made."
      />
      <RegistrationPage
        key={user.accountId}
        mode={{
          caller: 'account',
          accountId: user.accountId,
          ownParticipant: (participantId) => <NumberAlreadyYours participantId={participantId} />
        }}
      />
    </>
  );
}

/**
 * `409 identifier-already-yours`: the number offered is held by one of the account's own
 * participants, which survey-api names by id since the holder is the caller. Named here by the
 * account's own list and linked to where that participant's surveys start — the catalogue, or,
 * for a team that answers its questions first (`asksFirst`: routed nowhere yet, or by its firm
 * alone), the team's routing page, since the catalogue would only send it there (R7, block 1's
 * Q4). At the anchor the run stays there, so another number can follow; at a person's completion
 * the run is over and the chooser is drawn under it (Q24) — "below" is true of both.
 *
 * Both reads are made when the refusal arrives rather than with the page: nothing else on it
 * needs them. A read that fails, or a list that does not hold the id (a link removed a moment
 * ago), drops the name or the routing and keeps the sentence, which is true either way — and is
 * logged, since an unnamed participant is otherwise indistinguishable from the page working.
 */
function NumberAlreadyYours({ participantId }: { participantId: string }) {
  const [name, setName] = React.useState<Nullable<string>>(null);
  const [asks, setAsks] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    setName(null);
    setAsks(false);
    const failed = (what: string) => (error: unknown) => {
      if (controller.signal.aborted) return;
      console.error(`[Survey] Could not list the account’s ${what}:`, error);
    };
    surveyApi.myParticipants(controller.signal).then((participants) => {
      const holder = participants.find((held) => held.participantId === participantId);
      if (!holder) {
        console.warn('[Survey] The number’s holder is not on the account:', participantId);
      }
      setName(holder?.displayName ?? null);
    }, failed('participants'));
    surveyApi.myCycles(controller.signal).then((cycles) => {
      const held = teamsCycleOf(cycles, participantId);
      setAsks(held !== undefined && asksFirst(held.routing));
    }, failed('cycles'));
    return () => controller.abort();
  }, [participantId]);

  return (
    <Alert>
      <AlertTitle>That number is already on your account</AlertTitle>
      <AlertDescription>
        <p>
          {name ? `It is ${name}’s. ` : ''}Its surveys start from your list — or enter a
          different number below.
        </p>
        <Link
          to={asks ? teamRoutingPath(participantId) : PORTAL_PATHS.surveys}
          className="mt-2 inline-block font-medium text-[var(--color-royal-blue)] underline"
        >
          {asks
            ? `Answer ${name ? `${name}’s` : 'the team’s'} questions first`
            : name
              ? `Go to ${name}’s surveys`
              : 'Go to your surveys'}
        </Link>
      </AlertDescription>
    </Alert>
  );
}
